import type { ConnectedRecord } from "@/components/crm/ConnectedRecordsPanel";
import { leadContactDisplayName, type LeadDetailContactSnapshot } from "@/lib/crmLeadDetailDisplay";
import { crmAccountIsConverted, formatCrmStatusLabel } from "@/lib/crmDisplay";

export type { LeadDetailContactSnapshot };

export type LeadDetailConnectedRecordsInput = {
  lead: {
    id: string;
    leadName?: string | null;
    stage?: string | null;
    status?: string | null;
    accountId?: string | null;
    companyName?: string | null;
    companyDomain?: string | null;
    contacts: LeadDetailContactSnapshot[];
  };
  /** From GET /api/crm/accounts/:id when linked. */
  linkedAccountStatus?: string | null;
  linkedAccountName?: string | null;
  /** Resolved company title (same as Lead Account card). */
  companyDisplayName?: string | null;
  deals: Array<{ id: string; name?: string | null; stage?: string | null; accountId?: string }>;
  relatedLeads: Array<{ id: string; leadName?: string; stage?: string; status?: string }>;
};

/** Label for a Lead Account row in Connected Records (never the leadName slug). */
export function leadConnectedAccountLabel(input: {
  companyDisplayName?: string | null;
  linkedAccountName?: string | null;
  companyName?: string | null;
  companyDomain?: string | null;
  accountId?: string | null;
}): string {
  return (
    input.companyDisplayName?.trim() ||
    input.linkedAccountName?.trim() ||
    input.companyName?.trim() ||
    input.companyDomain?.trim() ||
    input.accountId?.trim() ||
    "Lead Account"
  );
}

function leadRecordIsConverted(status?: string | null): boolean {
  return (status || "").trim().toLowerCase() === "converted";
}

/** In-page navigation for pipeline Lead Account / Lead Contact rows (not CRM module URLs). */
export function leadDetailPipelineConnectedHref(
  leadId: string,
  target: "account" | { contactKey: string },
): string {
  const base = `/crm/leads/${encodeURIComponent(leadId)}`;
  if (target === "account") {
    return `${base}?focus=account`;
  }
  return `${base}?contact=${encodeURIComponent(target.contactKey)}`;
}

/** Which Connected Records row to highlight on lead detail. */
export function leadDetailConnectedSelectedRecordId(input: {
  leadId: string;
  accountId?: string | null;
  status?: string | null;
  linkedAccountStatus?: string | null;
  focusParam?: string | null;
  contactParam?: string | null;
}): string | null {
  const showLinkedAsCrm =
    leadRecordIsConverted(input.status) || crmAccountIsConverted(input.linkedAccountStatus);
  if (showLinkedAsCrm) {
    return input.leadId;
  }
  const focus = (input.focusParam || "").trim().toLowerCase();
  const contact = (input.contactParam || "").trim();
  if (contact && contact !== "0") {
    return contact;
  }
  if (focus === "account" && input.accountId?.trim()) {
    return input.accountId.trim();
  }
  if (input.accountId?.trim()) {
    return input.accountId.trim();
  }
  return null;
}

/** Classify lead pipeline vs CRM entities for Connected Records on lead detail. */
export function buildLeadDetailConnectedRecords(
  input: LeadDetailConnectedRecordsInput,
): {
  accounts: ConnectedRecord[];
  contacts: ConnectedRecord[];
  deals: ConnectedRecord[];
  leads: ConnectedRecord[];
  accountsTitle: string;
  contactsTitle: string;
  pipelineMode: boolean;
} {
  const { lead, linkedAccountStatus, linkedAccountName, companyDisplayName, deals, relatedLeads } =
    input;
  const accountLabel = leadConnectedAccountLabel({
    companyDisplayName,
    linkedAccountName,
    companyName: lead.companyName,
    companyDomain: lead.companyDomain,
    accountId: lead.accountId,
  });
  const accountIsCrm = crmAccountIsConverted(linkedAccountStatus);
  const leadIsConverted = leadRecordIsConverted(lead.status);
  const pipelineMode = !(leadIsConverted || accountIsCrm);

  const accounts: ConnectedRecord[] = [];
  const contacts: ConnectedRecord[] = [];
  const leads: ConnectedRecord[] = [];

  const hasAccountRow =
    Boolean(lead.accountId?.trim()) ||
    Boolean(accountLabel && accountLabel !== "Lead Account") ||
    Boolean(lead.companyName?.trim()) ||
    Boolean(lead.companyDomain?.trim());

  if (hasAccountRow) {
    const accountRowId = lead.accountId?.trim() || `lead-${lead.id}-account`;
    const accountBase: ConnectedRecord = {
      id: accountRowId,
      label: accountLabel,
      href: lead.accountId
        ? `/crm/accounts/${encodeURIComponent(lead.accountId)}`
        : `/crm/leads/${encodeURIComponent(lead.id)}`,
      icon: "building",
      meta: lead.companyDomain || "No domain",
    };

    if (pipelineMode) {
      leads.push({
        ...accountBase,
        href: leadDetailPipelineConnectedHref(lead.id, "account"),
        meta: "Lead Account",
      });
    } else {
      accounts.push(accountBase);
    }
  }

  for (const [index, contact] of lead.contacts.entries()) {
    const contactKey = (contact.contactId || String(index)).trim();
    if (!contactKey) continue;
    const label = leadContactDisplayName(contact);
    const contactBase: ConnectedRecord = {
      id: contact.contactId || `snap-${index}`,
      label,
      href:
        contact.contactId && lead.accountId
          ? `/crm/contacts/${encodeURIComponent(contact.contactId)}?accountId=${encodeURIComponent(lead.accountId)}`
          : `/crm/leads/${encodeURIComponent(lead.id)}?contact=${encodeURIComponent(contactKey)}`,
      icon: "user",
      meta: contact.email || "No email",
    };

    if (pipelineMode) {
      leads.push({
        ...contactBase,
        href: leadDetailPipelineConnectedHref(lead.id, { contactKey }),
        meta: "Lead Contact",
      });
    } else if (contact.contactId && lead.accountId) {
      contacts.push(contactBase);
    }
  }

  if (!pipelineMode) {
    const currentLeadLabel =
      companyDisplayName?.trim() || lead.leadName?.trim() || lead.companyName?.trim() || lead.id;
    leads.push({
      id: lead.id,
      label: currentLeadLabel,
      href: `/crm/leads/${encodeURIComponent(lead.id)}`,
      meta: `${formatCrmStatusLabel(lead.stage)} · ${formatCrmStatusLabel(lead.status)}`,
    });
  }

  for (const record of relatedLeads) {
    if (record.id === lead.id) continue;
    leads.push({
      id: record.id,
      label: record.leadName || record.id,
      href: `/crm/leads/${encodeURIComponent(record.id)}`,
      meta: `${formatCrmStatusLabel(record.stage)} · ${formatCrmStatusLabel(record.status)}`,
    });
  }

  const dealRecords = deals.map((deal) => ({
    id: deal.id,
    label: deal.name || deal.id,
    href: `/crm/deals/${encodeURIComponent(deal.id)}?accountId=${encodeURIComponent(lead.accountId || deal.accountId || "")}`,
    meta: formatCrmStatusLabel(deal.stage),
  }));

  return {
    accounts,
    contacts,
    deals: dealRecords,
    leads,
    accountsTitle: pipelineMode ? "Lead Accounts" : "Accounts",
    contactsTitle: pipelineMode ? "Lead Contacts" : "Contacts",
    pipelineMode,
  };
}
